import React from 'react';
import styled from 'styled-components';
import { generalInfo, greeting, history, mission, values } from './renewalData';
import {
  Block,
  BlockTitle,
  Card,
  Eyebrow,
  HairlineGrid,
  Inner,
  SectionHead,
  SectionNo,
  SectionShell,
  SectionTitle,
  token,
} from './renewalStyles';

/* ------------------------------------------------------------------ ABOUT BRIT */

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(340px, 100%), 1fr));
  gap: 64px;
  align-items: start;
`;

const AboutTitle = styled.h3`
  margin: 0 0 32px;
  font-size: clamp(30px, 3.6vw, 52px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
`;

const CeoName = styled.div`
  border-left: 4px solid #000;
  padding-left: 24px;

  strong {
    display: block;
    font-size: 18px;
    font-weight: 700;
  }

  span {
    display: block;
    font-size: 14px;
    color: ${token.muted};
    margin-top: 6px;
  }
`;

const Greeting = styled.div`
  font-size: 17px;
  line-height: 1.9;
  color: ${token.ink2};
  display: flex;
  flex-direction: column;
  gap: 20px;

  p {
    margin: 0;
  }
`;

/* ------------------------------------------------------------ MISSION & VALUES */

const MissionBlock = styled.div`
  background: #000;
  color: #fff;
  padding: clamp(32px, 5vw, 72px);
  margin-bottom: 2px;

  h3 {
    margin: 0 0 28px;
    font-size: clamp(22px, 2.4vw, 34px);
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: clamp(15px, 1.5vw, 19px);
    line-height: 1.6;
    color: #dadada;
  }
`;

const ValueCard = styled(Card)`
  padding: 36px 28px;

  em {
    display: block;
    font-style: normal;
    font-size: 13px;
    font-weight: 700;
    color: ${token.primary};
    margin-bottom: 14px;
  }

  strong {
    display: block;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.25;
    margin-bottom: 16px;
    white-space: pre-line;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.7;
    color: ${token.muted};
  }
`;

/* --------------------------------------------------------- GENERAL INFORMATION */

/* 3열 고정 — 900px 미만에서만 1열로 접습니다. */
const InfoList = styled(HairlineGrid).attrs({ as: 'dl', $cols: 'repeat(3, 1fr)' })`
  margin: 0;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #fff;
  padding: 32px 28px;
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};

  dt {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: ${token.primary};
    margin-bottom: 12px;
  }

  dd {
    margin: 0;
    font-size: ${({ $small, $address }) => ($small ? '18px' : $address ? '20px' : '22px')};
    font-weight: ${({ $small }) => ($small ? 700 : 800)};
    line-height: ${({ $small, $address }) => ($small || $address ? 1.5 : 'normal')};
    white-space: pre-line;
  }

  dd span {
    font-size: 0.8em;
    font-weight: 600;
    color: ${token.muted};
  }

  @media screen and (max-width: 900px) {
    grid-column: auto;
  }
`;

/* --------------------------------------------------------------------- HISTORY */

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 140px) 1fr;
  gap: clamp(20px, 4vw, 64px);
  padding: 32px 0;
  border-top: 1px solid ${token.border};

  > .year {
    font-size: clamp(28px, 3vw, 40px);
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${token.primary};
    line-height: 1;
  }

  > .body {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
    gap: 24px;
  }

  .events {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.7;
    white-space: pre-line;
  }

  .clients {
    font-size: 15px;
    line-height: 1.7;
    color: ${token.muted};
    white-space: pre-line;
  }
`;

/* --------------------------------------------------------- ORGANIZATION CHART */

const OrgPanel = styled.div`
  border: 1px solid ${token.border};
  background: #fff;
  padding: clamp(32px, 4vw, 56px);

  h3 {
    margin: 0 0 12px;
    font-size: clamp(24px, 2.6vw, 34px);
    font-weight: 800;
  }
`;

const OrgNote = styled.p`
  margin: 0 0 40px;
  font-size: 15px;
  color: ${token.muted};
`;

const OrgChart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  .head {
    background: #000;
    color: #fff;
    border-radius: 46px;
    padding: 18px 48px;
    font-size: 20px;
    font-weight: 800;
  }

  .line {
    width: 2px;
    height: 24px;
    background: #000;
  }

  .pill {
    border: 2px solid #000;
    border-radius: 46px;
    padding: 14px 36px;
    font-size: 16px;
    font-weight: 600;
  }

  .teams {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
    gap: 12px;
    width: 100%;
  }

  .teams > div {
    border: 2px solid #000;
    border-radius: 46px;
    padding: 14px 12px;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
  }
`;

export default function Overview() {
  return (
    <SectionShell id="overview">
      <Inner>
        <SectionHead>
          <SectionNo>01</SectionNo>
          <SectionTitle>OVERVIEW</SectionTitle>
        </SectionHead>

        <Block>
          <AboutGrid>
            <div>
              <Eyebrow>ABOUT BRIT</Eyebrow>
              <AboutTitle>
                혁신적인 IT 기술을 통해
                <br />더 나은 삶을 만들어가는 기업
              </AboutTitle>
              <CeoName>
                <strong>BRIT 대표이사 박정훈</strong>
                <span>(주)비알아이티 · Benefit Renew Information Technology</span>
              </CeoName>
            </div>
            <Greeting>
              {greeting.map(text => (
                <p key={text.slice(0, 16)}>{text}</p>
              ))}
            </Greeting>
          </AboutGrid>
        </Block>

        <Block data-reveal="true">
          <Eyebrow>MISSION &amp; VALUES</Eyebrow>
          <MissionBlock>
            <h3>MISSION</h3>
            <ul>
              {mission.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </MissionBlock>
          <HairlineGrid $min="240px">
            {values.map(value => (
              <ValueCard key={value.no}>
                <em>{value.no}</em>
                <strong>{value.title}</strong>
                <p>{value.desc}</p>
              </ValueCard>
            ))}
          </HairlineGrid>
        </Block>

        <Block>
          <Eyebrow>GENERAL INFORMATION</Eyebrow>
          <BlockTitle>BRIT 기업 일반 정보</BlockTitle>
          <InfoList>
            {generalInfo.map(info => (
              <InfoItem key={info.term} $wide={info.wide} $small={info.small} $address={info.address}>
                <dt>{info.term}</dt>
                <dd>
                  {info.desc}
                  {info.sub && (
                    <>
                      <br />
                      <span>{info.sub}</span>
                    </>
                  )}
                </dd>
              </InfoItem>
            ))}
          </InfoList>
        </Block>

        <Block>
          <Eyebrow>BRIT HISTORY</Eyebrow>
          <BlockTitle $mb="48px">연혁</BlockTitle>
          <div>
            {history.map(row => (
              <HistoryRow key={row.year}>
                <div className="year">{row.year}</div>
                <div className="body">
                  <div className="events">{row.events}</div>
                  <div className="clients">{row.clients}</div>
                </div>
              </HistoryRow>
            ))}
          </div>
        </Block>

        <Block $mb="0">
          <OrgPanel>
            <Eyebrow>ORGANIZATION CHART</Eyebrow>
            <h3>조직도</h3>
            <OrgNote>개발 2개 팀과 인프라·대외사업·경영지원으로 구성됩니다.</OrgNote>
            <OrgChart>
              <div className="head">대표 이사</div>
              <div className="line" />
              <div className="pill">경영지원팀</div>
              <div className="line" />
              <div className="teams">
                <div>솔루션 개발1팀</div>
                <div>솔루션 개발2팀</div>
                <div>인프라팀</div>
                <div>대외사업팀</div>
              </div>
            </OrgChart>
          </OrgPanel>
        </Block>
      </Inner>
    </SectionShell>
  );
}
